import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionEyebrow } from "@/components/Primitives";
import { CalendlyInline, CalendlyButton } from "@/components/Calendly";
import { submitContact } from "@/lib/api";

const initialState = {
  name: "",
  email: "",
  phone: "",
  goal: "1on1",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Thank you. Justin will reach out within 24 hours.");
      setForm(initialState);
      setShowForm(false);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please email xycjustin@gmail.com.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-ink-200 rounded-xl px-4 py-3 text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15 transition-all";

  return (
    <main data-testid="contact-page" className="bg-white text-ink-900">
      {/* Hero */}
      <section className="relative pt-36 pb-12 md:pb-16 hero-halo">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal className="col-span-12 lg:col-span-8">
              <SectionEyebrow testId="contact-page-eyebrow">Book your call</SectionEyebrow>
              <h1 className="mt-6 font-display font-light text-4xl sm:text-5xl md:text-6xl tracking-[-0.02em] leading-[1.05] text-ink-900 break-words">
                Pick a time.
                <br />
                <span className="text-brand-600">Sleep better soon.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1} className="col-span-12 lg:col-span-4">
              <p className="text-ink-600 leading-relaxed">
                Free 20-minute sleep assessment. No commitment. You&apos;ll
                leave with clarity on the bottleneck — and the next right move.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Calendly inline */}
      <section data-testid="contact-calendly-section" className="relative pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <CalendlyInline height={760} />
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="tel:+17782397162"
                data-testid="contact-phone"
                className="group flex items-center gap-4 p-5 rounded-2xl border border-ink-200 bg-white hover:border-brand-600 transition-all"
              >
                <div className="h-10 w-10 rounded-full bg-brand-50 flex items-center justify-center">
                  <Phone size={14} className="text-brand-600" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-ink-500 font-display">Phone</div>
                  <div className="font-display text-base mt-0.5 text-ink-900 group-hover:text-brand-600 transition-colors">778-239-7162</div>
                </div>
              </a>
              <a
                href="mailto:xycjustin@gmail.com"
                data-testid="contact-email"
                className="group flex items-center gap-4 p-5 rounded-2xl border border-ink-200 bg-white hover:border-brand-600 transition-all"
              >
                <div className="h-10 w-10 rounded-full bg-brand-50 flex items-center justify-center">
                  <Mail size={14} className="text-brand-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-ink-500 font-display">Email</div>
                  <div className="font-display text-base mt-0.5 text-ink-900 truncate group-hover:text-brand-600 transition-colors">xycjustin@gmail.com</div>
                </div>
              </a>
              <div className="flex items-center gap-4 p-5 rounded-2xl border border-ink-200 bg-white">
                <div className="h-10 w-10 rounded-full bg-brand-50 flex items-center justify-center">
                  <MapPin size={14} className="text-brand-600" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-ink-500 font-display">Based In</div>
                  <div className="font-display text-base mt-0.5 text-ink-900">Vancouver, BC · LA</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Optional message form */}
      <section className="relative py-20 bg-ink-50 border-t border-ink-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <div className="grid grid-cols-12 gap-6 items-end">
              <div className="col-span-12 lg:col-span-8">
                <SectionEyebrow testId="contact-message-eyebrow">Prefer to write?</SectionEyebrow>
                <h2 className="mt-5 font-display font-light text-2xl sm:text-3xl md:text-4xl tracking-tight text-ink-900 leading-[1.15] break-words">
                  Send a message instead. <span className="text-ink-500">Justin reads every one.</span>
                </h2>
              </div>
              <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
                <button
                  type="button"
                  data-testid="contact-toggle-form"
                  onClick={() => setShowForm((v) => !v)}
                  className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 text-sm font-medium"
                >
                  {showForm ? "Hide form" : "Open form"}
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${showForm ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
            </div>
          </Reveal>

          {showForm && (
            <Reveal>
              <form
                data-testid="contact-form"
                onSubmit={onSubmit}
                className="mt-10 surface p-6 md:p-8 grid grid-cols-12 gap-5"
              >
                <div className="col-span-12 md:col-span-6">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-2 font-display">Name *</label>
                  <input
                    data-testid="contact-input-name"
                    name="name" value={form.name} onChange={onChange}
                    type="text" placeholder="Your name" required
                    className={inputCls}
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-2 font-display">Email *</label>
                  <input
                    data-testid="contact-input-email"
                    name="email" value={form.email} onChange={onChange}
                    type="email" placeholder="you@company.com" required
                    className={inputCls}
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-2 font-display">Phone</label>
                  <input
                    data-testid="contact-input-phone"
                    name="phone" value={form.phone} onChange={onChange}
                    type="tel" placeholder="Optional"
                    className={inputCls}
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-2 font-display">Interested In</label>
                  <select
                    data-testid="contact-input-goal"
                    name="goal" value={form.goal} onChange={onChange}
                    className={`${inputCls} appearance-none`}
                  >
                    <option value="1on1">1-on-1 Sleep Coaching</option>
                    <option value="reset">30-Day Sleep & Gut Reset</option>
                    <option value="tools">Performance Optimization & Tools</option>
                    <option value="assessment">Free Sleep Assessment</option>
                  </select>
                </div>
                <div className="col-span-12">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-2 font-display">Tell me about your sleep *</label>
                  <textarea
                    data-testid="contact-input-message"
                    name="message" value={form.message} onChange={onChange}
                    rows={5} required
                    placeholder="What's the bottleneck? What have you already tried? What does success look like?"
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <div className="col-span-12 mt-2 flex flex-wrap items-center justify-between gap-4">
                  <p className="text-xs text-ink-500">No spam, ever.</p>
                  <motion.button
                    data-testid="contact-submit"
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    type="submit" disabled={submitting}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-brand-600 text-white font-medium hover:bg-brand-700 transition-all disabled:opacity-60 shadow-sm"
                  >
                    {submitting ? (
                      <><Loader2 size={16} className="animate-spin" /> Sending…</>
                    ) : (
                      <>Send Message <ArrowRight size={16} /></>
                    )}
                  </motion.button>
                </div>
              </form>
            </Reveal>
          )}

          {!showForm && (
            <Reveal>
              <div className="mt-8">
                <CalendlyButton variant="outline" testId="contact-calendly-secondary">
                  Or just book the call
                </CalendlyButton>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </main>
  );
};

export default Contact;
